// MIDTC Theme Tokens — all surface/text/border values are CSS variables
// so toggling data-theme on <html> switches the entire palette at once.

export const tokens = {
  surface: {
    page:      "var(--t-page)",
    panel:     "var(--t-panel)",
    panelHi:   "var(--t-panel-hi)",
    input:     "var(--t-input)",
    inputHi:   "var(--t-input-hi)",
    cardInput: "var(--t-card-input)",
    cardHead:  "var(--t-card-head)",
    mid:       "var(--t-surface-mid)",
    deep:      "var(--t-surface-deep)",
  },
  border: {
    soft:     "var(--t-border-soft)",
    softer:   "var(--t-border-softer)",
    mid:      "var(--t-border-mid)",
    strong:   "var(--t-border-strong)",
    // Accent-tinted borders follow the brand theme (they were Cyber-blue literals).
    indigo:   "color-mix(in srgb, var(--t-accent, #4f6fff) 32%, transparent)",
    indigoHi: "color-mix(in srgb, var(--t-accent, #4f6fff) 50%, transparent)",
    cardInput:"color-mix(in srgb, var(--t-accent, #4f6fff) 35%, transparent)",
  },
  text: {
    primary: "var(--t-text-primary)",
    body:    "var(--t-text-body)",
    muted:   "var(--t-text-muted)",
    label:   "var(--t-text-label)",
    faint:   "var(--t-text-faint)",
    deep:    "var(--t-text-deep)",
  },
  // Cyber accents: indigo→neon blue, violet→neon purple, cyan→electric.
  // Status hues (amber/green/red/orange/pink) are intentionally unchanged so
  // badges, status pills, and the analytical charts keep their current meaning.
  accent: {
    // `indigo` is the primary accent; routed through the brand-theme var so a
    // per-user theme re-skins accented UI. Falls back to the Cyber indigo.
    indigo:  "var(--t-accent, #4f6fff)", indigo2: "var(--t-accent-2, #7088ff)",
    violet:  "#7b61ff", violet2: "#9d8bff",
    blue:    "#5b8cff", blue2:   "#93b4ff",
    cyan:    "#00d4ff", sky:     "#38bdf8",
    amber:   "#fbbf24", amber2:  "#fde68a",
    orange:  "#fb923c", orange2: "#fdba74",
    green:   "#34d399", green2:  "#6ee7b7",
    red:     "#f87171", red2:    "#fca5a5",
    pink:    "#f472b6",
  },
  shadow: {
    panel:       "var(--t-shadow-panel)",
    panelHi:     "var(--t-shadow-panel-hi)",
    primaryGlow: "var(--t-accent-glow, 0 4px 18px rgba(79,111,255,0.45)), inset 0 1px 0 rgba(255,255,255,0.15)",
    cardSheen:   "linear-gradient(90deg,transparent,rgba(255,255,255,0.07),transparent)",
  },
  gradient: {
    primary:   "var(--t-accent-grad, linear-gradient(135deg,#4f6fff,#7b61ff))",
    panelHead: "var(--t-grad-panel-head)",
    listHead:  "var(--t-grad-list-head)",
    sidebar:   "var(--t-grad-sidebar)",
    rowSelect: "linear-gradient(120deg,var(--t-accent-soft,rgba(79,111,255,0.22)),var(--t-accent-softer,rgba(79,111,255,0.07)))",
  },
  // Themed corner radius: Cyber/Clean read soft, Bold/Terminal read sharp.
  // Strings (not numbers) so they resolve to the per-brand CSS vars — nothing
  // does arithmetic on these, which is what makes the swap safe. `pill` stays
  // numeric because it is a fixed "fully rounded" value, not a brand choice.
  radius: {
    sm:   "var(--t-radius-sm, 8px)",
    md:   "var(--t-radius-md, 12px)",
    lg:   "var(--t-radius-lg, 16px)",
    pill: 99,
  },
};

export const sx = {
  input: {
    background: tokens.surface.input,
    border: `1px solid ${tokens.border.mid}`,
    borderRadius: tokens.radius.sm,
    padding: "8px 12px",
    color: tokens.text.primary,
    fontSize: 13,
    width: "100%",
    fontFamily: "inherit",
    outline: "none",
  },
  cardInput: {
    background: tokens.surface.cardInput,
    border: `1px solid ${tokens.border.cardInput}`,
    borderRadius: tokens.radius.sm,
    padding: "8px 12px",
    color: tokens.text.primary,
    fontSize: 13,
    width: "100%",
    fontFamily: "inherit",
    outline: "none",
  },
  card: {
    background: tokens.surface.panelHi,
    border: `1px solid rgba(148,163,184,0.2)`,
    borderRadius: tokens.radius.md,
    boxShadow: tokens.shadow.panelHi,
  },
  cardHead: {
    padding: "10px 14px",
    background: tokens.surface.cardHead,
    borderBottom: `1px solid ${tokens.border.mid}`,
    // radius.md is a CSS var string, so it must NOT get a "px" suffix appended
    // (that would emit an invalid `var(...)px`). Interpolate it bare.
    borderRadius: `${tokens.radius.md} ${tokens.radius.md} 0 0`,
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.09em",
    color: tokens.text.label,
  },
  miniLabel: {
    fontSize: 10, fontWeight: 700,
    textTransform: "uppercase", letterSpacing: "0.07em",
    color: tokens.text.label,
  },
  // Cyber neon-glass buttons. The `cy-btn*` classNames wire up the global
  // hover-lift + light-sweep shimmer defined in index.css (inline styles can't
  // do :hover/::before), while these objects carry the static look.
  // Button personality is themed: weight / case / tracking / shadow all come
  // from per-brand vars, so Cyber glows, Clean is flat, Bold is uppercase with
  // a hard print offset, and Terminal is uppercase mono with a thin ring.
  buttonPrimary: {
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
    padding: "10px 18px", borderRadius: tokens.radius.md,
    background: tokens.gradient.primary, color: "#fff",
    border: "none", fontSize: 13,
    fontWeight:     "var(--t-btn-weight, 700)",
    textTransform:  "var(--t-btn-transform, none)",
    letterSpacing:  "var(--t-btn-letter, 0)",
    cursor: "pointer",
    boxShadow: "var(--t-btn-shadow, " + "0 4px 18px rgba(79,111,255,0.45))",
    fontFamily: "var(--t-font-btn, inherit)", position: "relative", overflow: "hidden",
    transition: "all 0.2s ease",
  },
  // Ghost/glass previously hardcoded the Cyber indigo, so they stayed blue on
  // every theme — now accent-driven like everything else.
  buttonGhost: {
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
    padding: "9px 15px", borderRadius: tokens.radius.md,
    background: "var(--t-accent-softer, rgba(79,111,255,0.08))",
    color: "var(--btn-remote-text, var(--t-text-body, #cdd9ef))",
    border: "1px solid var(--t-accent-soft, rgba(79,111,255,0.25))",
    fontSize: 13,
    fontWeight:    "var(--t-btn-weight, 600)",
    textTransform: "var(--t-btn-transform, none)",
    letterSpacing: "var(--t-btn-letter, 0)",
    cursor: "pointer",
    fontFamily: "var(--t-font-btn, inherit)", position: "relative", overflow: "hidden",
    transition: "all 0.2s ease",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05)",
  },
  buttonGlass: {
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
    padding: "9px 15px", borderRadius: tokens.radius.md,
    background: "var(--t-accent-softer, rgba(79,111,255,0.08))",
    color: "var(--btn-remote-text, var(--t-text-body, #cdd9ef))",
    border: "1px solid var(--t-accent-soft, rgba(79,111,255,0.25))",
    fontSize: 13,
    fontWeight:    "var(--t-btn-weight, 600)",
    textTransform: "var(--t-btn-transform, none)",
    letterSpacing: "var(--t-btn-letter, 0)",
    cursor: "pointer", fontFamily: "var(--t-font-btn, inherit)", position: "relative", overflow: "hidden",
    transition: "all 0.2s ease", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05)",
  },
  buttonDanger: {
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
    padding: "9px 15px", borderRadius: tokens.radius.md,
    background: "rgba(255,84,112,0.08)", color: "var(--btn-danger-text, #ff8a9b)",
    border: "1px solid rgba(255,84,112,0.25)", fontSize: 13, fontWeight: 600,
    cursor: "pointer", fontFamily: "var(--t-font-btn, inherit)", position: "relative", overflow: "hidden",
    transition: "all 0.2s ease",
  },
  badgeBase: {
    display: "inline-flex", alignItems: "center", gap: 5,
    padding: "3px 9px", borderRadius: tokens.radius.pill,
    fontSize: 10, fontWeight: 700,
    whiteSpace: "nowrap", letterSpacing: "0.02em",
  },
};
